import React from "react";
import { Link } from "react-router-dom";
import { cn } from "../../utils/classMerger";
import { SECTIONS, cardEligibility, cardsInSection, type CardDef, type EligibilityContext } from "./cards";
import type { CardSlug } from "./rules/deepLinks";

interface Props {
    ctx: EligibilityContext;
    onPick: (slug: CardSlug) => void;
}

const CardTile: React.FC<Props & { def: CardDef }> = ({ def, ctx, onPick }) => {
    const e = cardEligibility(def, ctx);
    const Icon = def.icon;
    return (
        <div className={cn("flex flex-col rounded-lg border bg-gray-50 shadow-sm transition", e.ok ? "hover:border-primary hover:shadow-md" : "opacity-80")}>
            {/* The button fills the card, so its empty space opens it too; the note's link stays outside it. */}
            <button
                type="button"
                disabled={!e.ok}
                onClick={() => onPick(def.slug)}
                className="flex flex-1 cursor-pointer items-start gap-3 rounded-lg p-5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed"
            >
                <span className="rounded-md bg-primary/10 p-2 text-primary"><Icon size={24} /></span>
                <span>
                    <span className="block text-lg font-semibold text-primary">{def.name}</span>
                    <span className="block text-sm text-gray-600">{def.sentence}</span>
                </span>
            </button>
            {!e.ok && e.checking && (
                <div role="status" aria-label="Checking your listings" className="-mt-2 mb-5 ml-5 h-5 w-2/3 rounded bg-gray-200 animate-skeleton" />
            )}
            {!e.ok && !e.checking && (
                <p className="-mt-2 px-5 pb-5 text-sm text-gray-500">
                    {/* Own element: the note reads as one string, apart from the " · Link" that follows it. */}
                    <span>{e.note}</span>
                    {e.link && <> · <Link to={e.link.to} className="underline">{e.link.label}</Link></>}
                </p>
            )}
        </div>
    );
};

const CardGrid: React.FC<Props> = ({ ctx, onPick }) => (
    <div className="space-y-8">
        {SECTIONS.map((s) => (
            <section key={s.id} aria-labelledby={`add-post-section-${s.id}`}>
                <h2 id={`add-post-section-${s.id}`} className="text-lg font-semibold text-primary">{s.title}</h2>
                <p className="mb-3 text-sm text-gray-600">{s.hint}</p>
                <div className="grid grid-cols-1 gap-4 min-[640px]:grid-cols-2 min-[1024px]:grid-cols-3">
                    {cardsInSection(s.id).map((def) => <CardTile key={def.slug} def={def} ctx={ctx} onPick={onPick} />)}
                </div>
            </section>
        ))}
    </div>
);
export default CardGrid;
